import { useState, type FormEvent } from 'react';
import { newId, useOutbox, type Stay } from '@dia/db';
import { useT } from '@dia/i18n';
import { Button } from '@dia/ui';
import { supabase } from '../supabase';
import { RequireAuth } from './lib/identity';
import { loadStays, useLoad } from './lib/data';
import { generateStayCode, statusForDay, validateStay, type StayForm, type StayProblem } from './lib/stays';
import { formatDay, panamaDateOf } from './lib/time';
import { Card, Field, inputClass, Notice, Page, Pill } from './lib/ui';

/**
 * Manual stay entry (Section 5, Phase 1). Managers and owners type a stay in;
 * Phase 2 replaces this with the Hostaway import.
 *
 * Stays are the one write in the ops app that does not go through the outbox:
 * the outbox only knows the tables a crew phone writes to, and a stay needs the
 * island's answer (a unique code) before anything else can point at it. It
 * needs signal, and says so. The lead guest, which can wait, does go through
 * the outbox. See NEEDS-FROM-FOUNDATION.md.
 */
export function JefeEstancias() {
  return <RequireAuth need="manager">{() => <StaysBody />}</RequireAuth>;
}

const blank = (): StayForm => ({
  tier: 'standard',
  arrives_on: panamaDateOf(),
  departs_on: panamaDateOf(),
  adults: 2,
  children: 0,
  arrival_flight: '',
  arrival_boat_time: '',
  dietary_notes: '',
  notes_internal: '',
  lead_first_name: '',
  lead_last_name: '',
});

function fromStay(s: Stay): StayForm {
  return {
    tier: s.tier,
    arrives_on: s.arrives_on,
    departs_on: s.departs_on,
    adults: s.adults,
    children: s.children,
    arrival_flight: s.arrival_flight ?? '',
    arrival_boat_time: s.arrival_boat_time ?? '',
    dietary_notes: s.dietary_notes ?? '',
    notes_internal: s.notes_internal ?? '',
    lead_first_name: '',
    lead_last_name: '',
  };
}

const PROBLEM_KEY = {
  dates: 'ops.stays.problem_dates',
  missing_dates: 'ops.stays.problem_missing',
  people: 'ops.stays.problem_people',
  lead_name: 'ops.stays.problem_lead',
} as const;

function StaysBody() {
  const { t, lang } = useT();
  const { enqueue } = useOutbox();
  const stays = useLoad(loadStays, 'stays', 60_000);
  const [editing, setEditing] = useState<Stay | 'new' | null>(null);
  const [form, setForm] = useState<StayForm>(blank());
  const [status, setStatus] = useState<Stay['status']>('upcoming');
  const [problems, setProblems] = useState<StayProblem[]>([]);
  const [message, setMessage] = useState<{ tone: 'ok' | 'danger' | 'warn'; key: 'ops.stays.saved' | 'ops.stays.need_signal' | 'ops.stays.denied' | 'error.generic' } | null>(null);
  const [busy, setBusy] = useState(false);

  const today = panamaDateOf();
  const set = <K extends keyof StayForm>(key: K, value: StayForm[K]) => setForm((f) => ({ ...f, [key]: value }));

  const open = (stay: Stay | 'new') => {
    setEditing(stay);
    setMessage(null);
    setProblems([]);
    if (stay === 'new') {
      setForm(blank());
      setStatus('upcoming');
    } else {
      setForm(fromStay(stay));
      setStatus(stay.status);
    }
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    const found = validateStay(form, editing !== 'new');
    setProblems(found);
    if (found.length > 0 || !editing) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setMessage({ tone: 'warn', key: 'ops.stays.need_signal' });
      return;
    }

    setBusy(true);
    setMessage(null);
    const row = {
      tier: form.tier,
      arrives_on: form.arrives_on,
      departs_on: form.departs_on,
      adults: form.adults,
      children: form.children,
      arrival_flight: form.arrival_flight.trim() || null,
      arrival_boat_time: form.arrival_boat_time.trim() || null,
      dietary_notes: form.dietary_notes.trim() || null,
      notes_internal: form.notes_internal.trim() || null,
      status,
    };

    try {
      if (editing === 'new') {
        let saved: { id: string } | null = null;
        let lastError: { code?: string; message: string } | null = null;
        // A code collision is one in a trillion, but a retry costs nothing.
        for (let attempt = 0; attempt < 3 && !saved; attempt += 1) {
          const { data, error } = await supabase
            .from('stays')
            .insert({ ...row, code: generateStayCode() })
            .select('id')
            .single();
          if (data) saved = data;
          else lastError = error;
          if (error && error.code !== '23505') break;
        }
        if (!saved) throw lastError ?? new Error('insert');
        await enqueue({
          id: newId(),
          table: 'guests',
          payload: {
            stay_id: saved.id,
            first_name: form.lead_first_name.trim(),
            last_name: form.lead_last_name.trim(),
            is_lead: true,
          },
        });
      } else {
        const { error } = await supabase.from('stays').update(row).eq('id', editing.id);
        if (error) throw error;
      }
      setMessage({ tone: 'ok', key: 'ops.stays.saved' });
      setEditing(null);
      await stays.reload();
    } catch (err) {
      const code = (err as { code?: string }).code;
      const text = err instanceof Error ? err.message : '';
      if (code === '42501' || /row-level security|permission/i.test(text)) setMessage({ tone: 'danger', key: 'ops.stays.denied' });
      else if (/fetch|network/i.test(text)) setMessage({ tone: 'warn', key: 'ops.stays.need_signal' });
      else setMessage({ tone: 'danger', key: 'error.generic' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page title={t('ops.stays.title')} back="/jefe">
      {message && (
        <div className="mb-4">
          <Notice tone={message.tone}>{t(message.key)}</Notice>
        </div>
      )}

      {!editing && (
        <>
          <Button size="lg" block onClick={() => open('new')}>
            {t('ops.stays.new')}
          </Button>
          <div className="mt-5 flex flex-col gap-3">
            {(stays.data ?? []).length === 0 && !stays.loading && <p className="text-inkMuted">{t('ops.stays.none')}</p>}
            {(stays.data ?? []).map((stay) => {
              const live = statusForDay(stay, today);
              return (
                <Card key={stay.id}>
                  <p className="m-0 flex flex-wrap items-center gap-2 font-semibold">
                    {t('ops.stays.dates_line', { from: formatDay(stay.arrives_on, lang), to: formatDay(stay.departs_on, lang) })}
                    <Pill tone={live === 'in_house' ? 'ok' : live === 'cancelled' ? 'danger' : 'neutral'}>
                      {t(`ops.stays.status_${live}`)}
                    </Pill>
                  </p>
                  <p className="m-0">{t('ops.stays.people_line', { adults: stay.adults, children: stay.children })}</p>
                  <p className="m-0 text-inkMuted">
                    {stay.tier === 'all_inclusive' ? t('ops.stays.tier_all') : t('ops.stays.tier_std')}
                    {'  '}
                    {t('ops.stays.code')}: <span className="font-mono font-semibold text-ink">{stay.code}</span>
                  </p>
                  <div className="mt-2">
                    <Button variant="secondary" onClick={() => open(stay)}>
                      {t('ops.stays.edit')}
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {editing && (
        <form onSubmit={save} className="flex flex-col gap-4">
          <h2 className="m-0 text-[1.2em] font-semibold">{editing === 'new' ? t('ops.stays.new') : t('ops.stays.edit')}</h2>

          <Field label={t('ops.stays.tier')}>
            <select value={form.tier} onChange={(e) => set('tier', e.target.value as StayForm['tier'])} className={inputClass}>
              <option value="all_inclusive">{t('ops.stays.tier_all')}</option>
              <option value="standard">{t('ops.stays.tier_std')}</option>
            </select>
          </Field>
          <Field label={t('ops.stays.arrives')}>
            <input type="date" value={form.arrives_on} onChange={(e) => set('arrives_on', e.target.value)} className={inputClass} required />
          </Field>
          <Field label={t('ops.stays.departs')}>
            <input type="date" value={form.departs_on} onChange={(e) => set('departs_on', e.target.value)} className={inputClass} required />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('ops.stays.adults')}>
              <input type="number" min={0} inputMode="numeric" value={form.adults} onChange={(e) => set('adults', Math.max(0, Number(e.target.value) || 0))} className={inputClass} />
            </Field>
            <Field label={t('ops.stays.children')}>
              <input type="number" min={0} inputMode="numeric" value={form.children} onChange={(e) => set('children', Math.max(0, Number(e.target.value) || 0))} className={inputClass} />
            </Field>
          </div>
          <Field label={t('ops.stays.flight')}>
            <input value={form.arrival_flight} onChange={(e) => set('arrival_flight', e.target.value)} className={inputClass} />
          </Field>
          <Field label={t('ops.stays.boat_time')}>
            <input value={form.arrival_boat_time} onChange={(e) => set('arrival_boat_time', e.target.value)} className={inputClass} />
          </Field>
          <Field label={t('ops.stays.dietary')}>
            <textarea rows={2} value={form.dietary_notes} onChange={(e) => set('dietary_notes', e.target.value)} className={inputClass} />
          </Field>
          <Field label={t('ops.stays.notes')}>
            <textarea rows={2} value={form.notes_internal} onChange={(e) => set('notes_internal', e.target.value)} className={inputClass} />
          </Field>

          {editing === 'new' ? (
            <>
              <Field label={t('ops.stays.lead_first')}>
                <input value={form.lead_first_name} onChange={(e) => set('lead_first_name', e.target.value)} className={inputClass} autoComplete="off" />
              </Field>
              <Field label={t('ops.stays.lead_last')}>
                <input value={form.lead_last_name} onChange={(e) => set('lead_last_name', e.target.value)} className={inputClass} autoComplete="off" />
              </Field>
              <p className="m-0 text-inkMuted">{t('ops.stays.lead_last_help')}</p>
            </>
          ) : (
            <Field label={t('ops.stays.status')}>
              <select value={status} onChange={(e) => setStatus(e.target.value as Stay['status'])} className={inputClass}>
                <option value="upcoming">{t('ops.stays.status_upcoming')}</option>
                <option value="in_house">{t('ops.stays.status_in_house')}</option>
                <option value="departed">{t('ops.stays.status_departed')}</option>
                <option value="cancelled">{t('ops.stays.status_cancelled')}</option>
              </select>
            </Field>
          )}

          {problems.map((p) => (
            <Notice key={p} tone="danger">
              {t(PROBLEM_KEY[p])}
            </Notice>
          ))}

          <Button type="submit" size="lg" block busy={busy} busyLabel={t('loading')}>
            {t('ops.stays.save')}
          </Button>
          <Button variant="secondary" block onClick={() => setEditing(null)}>
            {t('action.cancel')}
          </Button>
        </form>
      )}
    </Page>
  );
}
